import type { TenantVersions } from "@/api/tenantRuntime";

/** `<environment> · brain <8 chars or "?"> · <packName>@<packRevision>`. */
export function formatVersionsChip(versions: TenantVersions): string {
  const brain = versions.brainCommit?.slice(0, 8) ?? "?";
  const pack =
    versions.packRevision === null
      ? versions.packName
      : `${versions.packName}@${versions.packRevision}`;
  return `${versions.environment} · brain ${brain} · ${pack}`;
}

export type DefaultComparison = {
  state: "same" | "differs" | "unknown";
  built: string;
  served: string | null;
  tenant: string;
};

/** `builtOn` is the test version's own base revision; without one, the default the Editor builds on. */
export function compareDefaults(
  versions: TenantVersions | undefined,
  tenant: string | undefined,
  builtOn: string | null | undefined,
): DefaultComparison | null {
  const built = builtOn ?? versions?.editorDefaultRevision ?? null;
  if (versions === undefined || tenant === undefined || built === null) {
    return null;
  }
  const served = versions.servedDefaultRevision;
  const state =
    served === null ? "unknown" : served === built ? "same" : "differs";
  return { state, built, served, tenant };
}

export function defaultSentence(c: DefaultComparison): string {
  if (c.state === "same") return `Default ${c.built}, same as ${c.tenant}.`;
  if (c.state === "unknown") {
    return `Built on default ${c.built}. The default ${c.tenant} serves is unknown.`;
  }
  return `The defaults differ: this test version was built on ${c.built}, but ${c.tenant} serves ${c.served}. Reliv Labs runs ${c.tenant}'s own default ${c.served}, with your edits on top.`;
}

export function defaultChip(c: DefaultComparison): string {
  if (c.state === "same") return `default ${c.built} (same as ${c.tenant})`;
  if (c.state === "unknown") return `default ${c.built} · served default unknown`;
  return `defaults differ · built ${c.built} · ${c.tenant} serves ${c.served}`;
}
